package at.theatervogelweide.admin.web.screens.acts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.ImageCategory
import at.theatervogelweide.admin.contract.acts.ActListEntry
import at.theatervogelweide.admin.contract.acts.ActService
import at.theatervogelweide.admin.contract.acts.onTicketPage
import at.theatervogelweide.admin.web.AdminApi
import at.theatervogelweide.admin.web.Route
import at.theatervogelweide.admin.web.components.AdminBadge
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminPageBody
import at.theatervogelweide.admin.web.components.AdminPageHeader
import at.theatervogelweide.admin.web.components.AdminSelectableList
import at.theatervogelweide.admin.web.components.AdminTextField
import at.theatervogelweide.admin.web.components.BadgeVariant
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import at.theatervogelweide.admin.web.components.SelectableRow
import at.theatervogelweide.admin.web.rpcCall
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.rpc.RpcClient
import kotlinx.rpc.withService
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Clock
import kotlin.time.Duration.Companion.minutes

private val card = Bem("admin-card")
private val listPanel = Bem("admin-list-panel")
private val actList = Bem("admin-act-list")
private val alert = Bem("admin-alert")

internal fun now(): LocalDateTime = Clock.System.now().toLocalDateTime(TimeZone.UTC)

/** Where the act stands on the ticket page: shown there now ([onTicketPage]), or announced later. */
private fun ActListEntry.badge(onTicketPage: ActListEntry?, now: LocalDateTime): Pair<String, BadgeVariant>? {
    val schedule = ticketSchedule ?: return null
    return when {
        id == onTicketPage?.id -> "Kartenseite" to BadgeVariant.PUBLIC
        now < schedule.announcedFrom -> "Geplant" to BadgeVariant.PLANNED
        else -> null
    }
}

/** The search matches the title and the year, word by word. */
internal fun ActListEntry.matches(query: String): Boolean {
    val text = "$title $year".lowercase()
    return query.trim().lowercase().split(Regex("\\s+")).filter { it.isNotEmpty() }.all { it in text }
}

/**
 * The list of Stücke & Termine as it was left for an editor: the acts last loaded, the search and the scroll position,
 * so the list comes back as it was.
 */
class ActsListState {
    var entries: List<ActListEntry>? by mutableStateOf(null)
    var query: String by mutableStateOf("")

    /** Where the window was scrolled to when an editor opened; taken once on return. */
    internal var scrollY: Double? = null
}

/**
 * Stücke & Termine (#22): all acts by year, newest first, like the public archive. [onNavigate] opens an editor;
 * [list] keeps the list as it was meanwhile.
 */
@Composable
fun ActsScreen(rpc: RpcClient, api: AdminApi, list: ActsListState, onNavigate: (Route) -> Unit) {
    val service = remember(rpc) { rpc.withService<ActService>() }
    var entries by list::entries
    var error by remember { mutableStateOf<String?>(null) }
    var query by list::query

    // the acts last loaded show at once, where the list was left; the fresh ones follow
    LaunchedEffect(Unit) {
        list.scrollY?.takeIf { entries != null }?.let { window.scrollTo(0.0, it) }
        list.scrollY = null
    }

    LaunchedEffect(service) {
        when (val result = rpcCall { service.list() }) {
            is AdminResult.Success -> entries = result.value
            is AdminResult.Failure -> error = result.error.message
        }
    }

    fun open(route: Route) {
        list.scrollY = window.scrollY
        onNavigate(route)
    }

    // moves the badges on when an act is announced or its last performance starts
    var now by remember { mutableStateOf(now()) }
    LaunchedEffect(Unit) {
        while (true) {
            delay(1.minutes)
            now = now()
        }
    }

    AdminPageHeader("Stücke & Termine") {
        AdminButton(variant = ButtonVariant.PRIMARY, onClick = { open(Route.Acts(new = true)) }) { Text("+ Stück") }
    }

    AdminPageBody {
        Section({
            classes(card.block, listPanel.block, actList.block)
            attr("aria-label", "Liste der Stücke")
        }) {
            Div({ classes(actList.element("search").className) }) {
                AdminTextField(
                    id = "stuecke-suche",
                    label = "Suche",
                    value = query,
                    onValueChange = { query = it },
                    type = InputType.Search,
                    optional = true,
                    placeholder = "Titel oder Jahr",
                    autocomplete = "off",
                )
            }

            error?.let {
                P({
                    classes(listPanel.element("message").className, alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }

            val all = entries
            val shown = all.orEmpty().filter { it.matches(query) }
            val onTicketPage = all.orEmpty().onTicketPage(now)
            when {
                all == null -> if (error == null) P({ classes(listPanel.element("message").className) }) { Text("Lade …") }
                all.isEmpty() -> P({ classes(listPanel.element("message").className) }) {
                    Text("Noch keine Stücke. Lege mit „+ Stück“ das erste an.")
                }

                shown.isEmpty() -> P({
                    classes(listPanel.element("message").className)
                    attr("role", "status")
                }) { Text("Kein Stück passt zu „${query.trim()}“.") }

                else -> shown.groupBy { it.year }.forEach { (year, acts) ->
                    H2({ classes(actList.element("year").className) }) { Text(year.toString()) }
                    AdminSelectableList(
                        rows = acts.map { act ->
                            SelectableRow(
                                key = act.id,
                                title = act.title,
                                meta = act.metaText(),
                                thumbnailUrl = act.mainImageUuid?.let { api.imageUrl(ImageCategory.ACTS, it) },
                                badge = act.badge(onTicketPage, now)?.let { (text, variant) -> { AdminBadge(text, variant) } },
                            )
                        },
                        selected = null,
                        onSelect = { id -> open(Route.Acts(id)) },
                    )
                }
            }
        }
    }
}
